import type { Env, Environment } from '@sparklayer-io/spark-dash-core/context/env-context';
import { ApiResponseError } from '@sparklayer-io/spark-dash-core/types/errors';
import type { ApiHandlerContract } from '@sparklayer-io/spark-dash-core/types/handlers/api';
import { t } from 'i18next';
import { create, type StoreApi, type UseBoundStore } from 'zustand';
import { useNotifications } from './notifications';
import type { HostUser } from './session-types';

/**
 * The environment a site defaults to when the url doesn't name one. Partner teams only have a
 * test environment, everyone else lands on live. Undefined for a site this session doesn't know,
 * so the caller picks a default and lets `env`'s `loadEnv` fall back if it guessed wrong.
 */
export function defaultSiteEnv(user: HostUser | null, siteId: string): Environment | undefined {
  const currentTeam = user?.currentTeam;
  const isPartner =
    currentTeam?.slugged_name === siteId
      ? currentTeam.is_partner
      : user?.availableTeams?.find((team) => team.site_id === siteId)?.is_partner;

  if (isPartner === undefined) {
    return undefined;
  }

  return isPartner ? 'test' : 'live';
}

/** What to do when a site rejects the environment asked for. Absent means fail as asked. */
export type EnvFallback =
  /** Keep whichever environment loads - the one asked for was ours to pick. */
  | 'any'
  /** Keep it only if the site turns out to be a partner's; they have no live environment. */
  | 'partner';

type LoadEnvOptions = {
  fallback?: EnvFallback;
  /** Refetch even if this exact site/env pair is already loaded - for a caller polling
   * readiness (e.g. `/getting-ready`), where "already loaded" is exactly what's being re-checked. */
  force?: boolean;
  /** Skip the failure notification. For a caller probing whether a url's site exists at all, which
   * handles the miss itself rather than showing the user an error for a url they mistyped. */
  quiet?: boolean;
};

export type EnvState = {
  siteId: string | null;
  siteEnv: string | null;
  env: Env | null;
  /** Set while a load is in flight. */
  loading: boolean;
  loadEnv(input?: { siteId: string; siteEnv: string }, options?: LoadEnvOptions): Promise<void>;
  reset(): void;
};

export type EnvStore = UseBoundStore<StoreApi<EnvState>>;

export function createEnvStore(apiHandler: ApiHandlerContract): EnvStore {
  const initialState = {
    siteId: null,
    siteEnv: null,
    env: null,
    loading: false,
  } satisfies Partial<EnvState>;

  return create<EnvState>()((set, get) => ({
    ...initialState,

    async loadEnv(input, options = {}) {
      let { siteId, siteEnv } = get();
      if (input?.siteId) {
        siteId = input.siteId;
      }
      if (input?.siteEnv) {
        siteEnv = input.siteEnv;
      }

      // Avoid loading environment if site id and site env have not changed. `env !== null` is
      // part of the check because a failed load keeps the site it was for, and that has to be
      // retried rather than skipped.
      if (
        !options.force &&
        input !== undefined &&
        get().env !== null &&
        get().siteId === siteId &&
        get().siteEnv === siteEnv
      ) {
        return;
      }

      if (!siteId || !siteEnv) {
        return;
      }

      set({ loading: true });
      try {
        const attempt = await fetchEnv(apiHandler, siteId, siteEnv);
        let env = attempt.env;

        // Partner sites only have a test environment, so asking one for live has to fall back
        // rather than dead-end on the getting ready screen. Only when the site itself rejected
        // the environment - a request that failed on the way must not move the user elsewhere.
        if (env === null && options.fallback && isSiteRejected(attempt.status)) {
          const otherEnv = siteEnv === 'live' ? 'test' : 'live';
          const fallback = await fetchEnv(apiHandler, siteId, otherEnv);

          // `team.is_partner` is the backend's own answer for this exact site, so a `partner`
          // fallback cannot move a site that isn't one - it fails as asked instead.
          if (fallback.env && (options.fallback === 'any' || fallback.env.team.is_partner)) {
            siteEnv = otherEnv;
            env = fallback.env;
          }
        }

        if (env === null) {
          // Hold on to the site that was asked for, but not to the env we had: whether the load
          // was refused or just failed, the team we hold is no longer known to match the site in
          // the url, and leaving it in place puts one team's name, billing and store id on screen
          // under another team's site id. The router sends the user to `/getting-ready`, which
          // retries.
          set({ siteId, siteEnv, env: null });

          if (!options.quiet) {
            useNotifications
              .getState()
              .notify(t('notifications:titles.error'), t('errors:generic'), 'error');
          }
          return;
        }

        set({ siteId, siteEnv, env });
      } finally {
        set({ loading: false });
      }
    },

    reset() {
      set(initialState);
    },
  }));
}

/** The env for a site, plus the status of the attempt so the caller can tell why it failed. */
async function fetchEnv(
  apiHandler: ApiHandlerContract,
  siteId: string,
  siteEnv: string,
): Promise<{ env: Env | null; status?: number }> {
  try {
    const env = await apiHandler.fetch<Env>({ method: 'GET', path: `/${siteId}/${siteEnv}` });
    // A response with no body resolves as `undefined`; normalize it so the caller's `env === null`
    // miss-check catches it rather than reading `db_created` off nothing.
    return { env: env ?? null };
  } catch (err) {
    return { env: null, status: err instanceof ApiResponseError ? err.status : undefined };
  }
}

/** The site itself was rejected, as opposed to the request failing on the way there. */
function isSiteRejected(status?: number): boolean {
  return status === 403 || status === 404;
}
